import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import api from '@/services/api.service';
import assetService from '@/services/asset.service';
import assetMaintenanceService from '@/services/asset-maintenance.service';
import { formatAssetOption } from '@/utils/assetDisplay';

const { Title } = Typography;
const { TextArea } = Input;

function unwrapArray<T>(res: unknown): T[] {
  const ax = (res as { data?: { data?: T[] } })?.data;
  return Array.isArray(ax) ? (ax as T[]) : Array.isArray((ax as { data?: T[] })?.data) ? ((ax as { data: T[] }).data ?? []) : [];
}

function useAssetOptions() {
  const q = useQuery({ queryKey: ['assets', 'pick'], queryFn: () => assetService.list() });
  const options = useMemo(
    () => (q.data ?? []).map((a) => ({ value: a.id, label: formatAssetOption(a) })),
    [q.data]
  );
  return { options, loading: q.isLoading };
}

function useEmployeeOptions() {
  const q = useQuery({
    queryKey: ['emp-maint'],
    queryFn: async () => {
      const res = await api.get('/hr/employees');
      return unwrapArray<{ id: string; full_name: string; employee_code?: string }>(res);
    },
  });
  const options = useMemo(
    () =>
      (q.data ?? []).map((e) => ({
        value: e.id,
        label: `${e.employee_code || ''} ${e.full_name}`.trim(),
      })),
    [q.data]
  );
  return { options };
}

function useSupplierOptions() {
  const q = useQuery({
    queryKey: ['supp-maint'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrapArray<{ id: string; supplier_name: string; supplier_code: string }>(res);
    },
  });
  const options = useMemo(
    () =>
      (q.data ?? []).map((s) => ({
        value: s.id,
        label: `${s.supplier_code} — ${s.supplier_name}`,
      })),
    [q.data]
  );
  return { options };
}

export const AssetMaintenanceCaseFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options: assetOptions } = useAssetOptions();
  const { options: empOptions } = useEmployeeOptions();

  const existingQ = useQuery({
    queryKey: ['asset-maintenance-case', id],
    queryFn: () => assetMaintenanceService.getCase(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        asset_id: sp.get('asset') || undefined,
        priority: 'normal',
      });
      return;
    }
    const r = existingQ.data;
    if (!r) return;
    form.setFieldsValue({
      title: r.title,
      description: r.description,
      priority: r.priority,
      status: r.status,
      case_type: (r as any).case_type || 'OTHER',
      assigned_employee_id: r.assigned_employee_id,
      labor_cost: r.labor_cost,
      material_cost: r.material_cost,
      other_cost: r.other_cost,
      resolution_notes: r.resolution_notes,
    });
  }, [isNew, existingQ.data, form, sp]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetMaintenanceService.createCase(body);
      return assetMaintenanceService.updateCase(id!, body);
    },
    onSuccess: (created: any) => {
      message.success(isNew ? 'Work request created' : 'Work request updated');
      if (isNew && created?.id) {
        navigate(`/asset/workshop/jobs/${created.id}`);
      } else {
        navigate('/asset/maintenance');
      }
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  if (!isNew && existingQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New work request' : 'Edit work request'}
          documentNumber={existingQ.data?.case_number ?? ''}
        />
      }
    >
      <Title level={5}>Corrective maintenance</Title>
      {!isNew && id && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, maxWidth: 640 }}
          message="Workshop / Garage"
          description="Intake, holds, parts, labour, QC and release are managed in the Workshop Job Workspace. This form remains the authoritative Maintenance Work Order (MNT)."
          action={
            <Link to={`/asset/workshop/jobs/${id}`}>
              <Button type="primary" size="small">
                Open workshop job
              </Button>
            </Link>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          if (isNew) {
            mut.mutate({
              asset_id: v.asset_id,
              title: v.title,
              description: v.description ?? null,
              priority: v.priority || 'normal',
              case_type: v.case_type || 'CORRECTIVE_MAINTENANCE',
              assigned_employee_id: v.assigned_employee_id || null,
              labor_cost: v.labor_cost != null ? Number(v.labor_cost) : null,
              material_cost: v.material_cost != null ? Number(v.material_cost) : null,
              other_cost: v.other_cost != null ? Number(v.other_cost) : null,
            });
          } else {
            mut.mutate({
              title: v.title,
              description: v.description ?? null,
              priority: v.priority,
              status: v.status,
              case_type: v.case_type,
              assigned_employee_id: v.assigned_employee_id || null,
              labor_cost: v.labor_cost != null ? Number(v.labor_cost) : null,
              material_cost: v.material_cost != null ? Number(v.material_cost) : null,
              other_cost: v.other_cost != null ? Number(v.other_cost) : null,
              resolution_notes: v.resolution_notes ?? null,
            });
          }
        }}
      >
        {isNew && (
          <Form.Item name="asset_id" label="Asset" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={assetOptions} />
          </Form.Item>
        )}
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="priority" label="Priority" initialValue="normal">
          <Select options={['low', 'normal', 'high', 'urgent'].map((p) => ({ value: p, label: p }))} />
        </Form.Item>
        <Form.Item name="case_type" label="Case type" initialValue="CORRECTIVE_MAINTENANCE">
          <Select
            options={[
              { value: 'PREVENTIVE_MAINTENANCE', label: 'Preventive maintenance' },
              { value: 'CORRECTIVE_MAINTENANCE', label: 'Corrective maintenance' },
              { value: 'BREAKDOWN', label: 'Breakdown' },
              { value: 'INSPECTION_FINDING', label: 'Inspection finding' },
              { value: 'ACCIDENT_REPAIR', label: 'Accident repair' },
              { value: 'COMPONENT_REPAIR', label: 'Component repair' },
              { value: 'SERVICE', label: 'Service' },
              { value: 'OTHER', label: 'Other' },
            ]}
          />
        </Form.Item>
        {!isNew && (
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={['open', 'assigned', 'in_progress', 'completed', 'cancelled'].map((p) => ({
                value: p,
                label: p.replace(/_/g, ' '),
              }))}
            />
          </Form.Item>
        )}
        <Form.Item name="assigned_employee_id" label="Assign to (optional)">
          <Select allowClear showSearch optionFilterProp="label" options={empOptions} />
        </Form.Item>
        <Form.Item name="labor_cost" label="Labor cost">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="material_cost" label="Material cost">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="other_cost" label="Other cost">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        {!isNew && (
          <Form.Item name="resolution_notes" label="Resolution / notes">
            <TextArea rows={3} />
          </Form.Item>
        )}
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button onClick={() => navigate('/asset/maintenance')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const AssetMaintenancePmFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options: assetOptions } = useAssetOptions();

  const existingQ = useQuery({
    queryKey: ['asset-maintenance-pm', id],
    queryFn: () => assetMaintenanceService.getPm(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        asset_id: sp.get('asset') || undefined,
        frequency_unit: 'months',
        frequency_value: 3,
        is_active: true,
      });
      return;
    }
    const r = existingQ.data;
    if (!r) return;
    form.setFieldsValue({
      title: r.title,
      frequency_unit: r.frequency_unit,
      frequency_value: r.frequency_value,
      start_date: r.start_date ? dayjs(r.start_date) : undefined,
      next_due_date: r.next_due_date ? dayjs(r.next_due_date) : undefined,
      is_active: r.is_active,
      remarks: r.remarks,
      checklist: (r.checklist_json ?? []).join('\n'),
    });
  }, [isNew, existingQ.data, form, sp]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetMaintenanceService.createPm(body);
      return assetMaintenanceService.updatePm(id!, body);
    },
    onSuccess: () => {
      message.success(isNew ? 'PM plan created' : 'PM plan updated');
      navigate('/asset/maintenance?tab=pm');
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  if (!isNew && existingQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New PM plan' : 'Edit PM plan'}
          documentNumber={existingQ.data?.title ?? ''}
        />
      }
    >
      <Title level={5}>Preventive maintenance plan</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          const lines = String(v.checklist || '')
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean);
          const body: Record<string, unknown> = {
            title: v.title,
            frequency_unit: v.frequency_unit,
            frequency_value: v.frequency_value,
            start_date: v.start_date ? (v.start_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            next_due_date: v.next_due_date ? (v.next_due_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            checklist_json: lines.length ? lines : null,
            is_active: v.is_active,
            remarks: v.remarks ?? null,
          };
          if (isNew) body.asset_id = v.asset_id;
          mut.mutate(body);
        }}
      >
        {isNew && (
          <Form.Item name="asset_id" label="Asset" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={assetOptions} />
          </Form.Item>
        )}
        <Form.Item name="title" label="Title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="frequency_unit" label="Frequency" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'days', label: 'Days' },
              { value: 'weeks', label: 'Weeks' },
              { value: 'months', label: 'Months' },
              { value: 'years', label: 'Years' },
            ]}
          />
        </Form.Item>
        <Form.Item name="frequency_value" label="Every (count)" rules={[{ required: true }]}>
          <InputNumber min={1} max={9999} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="start_date" label="Start date (optional)">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="next_due_date" label="Next due (optional)">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item name="checklist" label="Checklist (one line per item)">
          <TextArea rows={4} placeholder="Oil level&#10;Belts…" />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button onClick={() => navigate('/asset/maintenance?tab=pm')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const AssetMaintenancePmCompletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const existingQ = useQuery({
    queryKey: ['asset-maintenance-pm', id],
    queryFn: () => assetMaintenanceService.getPm(id!),
    enabled: Boolean(id),
  });

  const mut = useMutation({
    mutationFn: (body: Record<string, unknown>) => assetMaintenanceService.completePm(id!, body),
    onSuccess: () => {
      message.success('PM completion recorded');
      navigate('/asset/maintenance?tab=pm');
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Record PM completion"
          documentNumber={existingQ.data?.title ?? ''}
        />
      }
    >
      <Title level={5}>Mark preventive plan complete</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) =>
          mut.mutate({
            next_due_date: v.next_due_date ? (v.next_due_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            remarks: (v.remarks as string) || null,
          })
        }
      >
        <Form.Item name="next_due_date" label="Next due (optional)">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks (optional)">
          <TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            Complete
          </Button>
          <Button onClick={() => navigate('/asset/maintenance?tab=pm')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const AssetMaintenanceAmcFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options: assetOptions } = useAssetOptions();
  const { options: supplierOptions } = useSupplierOptions();

  const existingQ = useQuery({
    queryKey: ['asset-maintenance-amc', id],
    queryFn: () => assetMaintenanceService.getAmc(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ asset_id: sp.get('asset') || undefined, is_active: true });
      return;
    }
    const r = existingQ.data;
    if (!r) return;
    form.setFieldsValue({
      supplier_id: r.supplier_id,
      contract_number: r.contract_number,
      start_date: r.start_date ? dayjs(r.start_date) : undefined,
      end_date: r.end_date ? dayjs(r.end_date) : undefined,
      response_sla_hours: r.response_sla_hours,
      is_active: r.is_active,
      notes: r.notes,
    });
  }, [isNew, existingQ.data, form, sp]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetMaintenanceService.createAmc(body);
      return assetMaintenanceService.updateAmc(id!, body);
    },
    onSuccess: () => {
      message.success(isNew ? 'AMC created' : 'AMC updated');
      navigate('/asset/maintenance?tab=amc');
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New AMC' : 'Edit AMC'}
          documentNumber={existingQ.data?.contract_number ?? ''}
        />
      }
    >
      <Title level={5}>Annual maintenance contract</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          const body: Record<string, unknown> = {
            supplier_id: v.supplier_id || null,
            contract_number: v.contract_number || null,
            start_date: v.start_date ? (v.start_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            end_date: v.end_date ? (v.end_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            response_sla_hours: v.response_sla_hours == null ? null : Number(v.response_sla_hours),
            is_active: v.is_active,
            notes: v.notes || null,
          };
          if (isNew) body.asset_id = v.asset_id;
          mut.mutate(body);
        }}
      >
        {isNew && (
          <Form.Item name="asset_id" label="Asset" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={assetOptions} />
          </Form.Item>
        )}
        <Form.Item name="supplier_id" label="Vendor (supplier)">
          <Select allowClear showSearch optionFilterProp="label" options={supplierOptions} />
        </Form.Item>
        <Form.Item name="contract_number" label="Contract / reference #">
          <Input />
        </Form.Item>
        <Form.Item name="start_date" label="Start">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="end_date" label="End">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="response_sla_hours" label="Response SLA (hours)">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button onClick={() => navigate('/asset/maintenance?tab=amc')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};
